/**
 * `PHONE_COUNTRIES` — static list of dial-code metadata for the `PhoneInput`
 * country-code picker. Mirrors the dial-code set used by the legacy CRA site
 * (`react-phone-input-2`, see `REACTJS/src/app/components/Form.jsx`) so a value
 * the old site saved (e.g. `+919876543210`) round-trips cleanly.
 *
 * Ordering: a hand-picked "popular first" block (US, India, UK, Canada, AU)
 * followed by the rest alphabetically by country name — gives a sensible
 * default zone for users without forcing them to scroll past every country to
 * find a common one.
 *
 * The picker is the source of truth for `dial`; the form keeps the combined
 * `+<dial><number>` string in a single field (matching the legacy payload).
 */
export interface PhoneCountry {
  /** ISO-3166-1 alpha-2 code, e.g. `'US'`, `'IN'`. Uppercased. */
  code: string;
  /** International dial code with leading `+`, e.g. `'+1'`, `'+91'`. */
  dial: string;
  /** Long-form country name as shown in the dropdown options. */
  name: string;
  /** Emoji flag (renders fine on macOS / iOS / most modern Windows). */
  flag: string;
  /**
   * Allowed national-number length (digits only, no dial code). Use a
   * single number for an exact length (e.g. `10` for IN / US), or a
   * `[min, max]` tuple for a range (e.g. `[10, 11]` for GB).
   *
   * Omitted countries fall back to the E.164 baseline (7–15 digits).
   */
  mobileLen?: number | [number, number];
}

/** A small "popular first" block — order matters here, don't re-sort. */
const POPULAR: PhoneCountry[] = [
  { code: 'IN', dial: '+91', name: 'India', flag: '🇮🇳', mobileLen: 10 },
  { code: 'US', dial: '+1', name: 'United States', flag: '🇺🇸', mobileLen: 10 },
  { code: 'GB', dial: '+44', name: 'United Kingdom', flag: '🇬🇧', mobileLen: 10 },
  { code: 'CA', dial: '+1', name: 'Canada', flag: '🇨🇦', mobileLen: 10 },
  { code: 'AU', dial: '+61', name: 'Australia', flag: '🇦🇺', mobileLen: 9 },
];

/** Rest of the world — alphabetical by country name. */
const REST: PhoneCountry[] = [
  { code: 'AF', dial: '+93', name: 'Afghanistan', flag: '🇦🇫' },
  { code: 'AL', dial: '+355', name: 'Albania', flag: '🇦🇱' },
  { code: 'DZ', dial: '+213', name: 'Algeria', flag: '🇩🇿' },
  { code: 'AR', dial: '+54', name: 'Argentina', flag: '🇦🇷' },
  { code: 'AM', dial: '+374', name: 'Armenia', flag: '🇦🇲' },
  { code: 'AT', dial: '+43', name: 'Austria', flag: '🇦🇹' },
  { code: 'AZ', dial: '+994', name: 'Azerbaijan', flag: '🇦🇿' },
  { code: 'BH', dial: '+973', name: 'Bahrain', flag: '🇧🇭' },
  { code: 'BD', dial: '+880', name: 'Bangladesh', flag: '🇧🇩' },
  { code: 'BY', dial: '+375', name: 'Belarus', flag: '🇧🇾' },
  { code: 'BE', dial: '+32', name: 'Belgium', flag: '🇧🇪' },
  { code: 'BO', dial: '+591', name: 'Bolivia', flag: '🇧🇴' },
  { code: 'BR', dial: '+55', name: 'Brazil', flag: '🇧🇷' },
  { code: 'BG', dial: '+359', name: 'Bulgaria', flag: '🇧🇬' },
  { code: 'KH', dial: '+855', name: 'Cambodia', flag: '🇰🇭' },
  { code: 'CM', dial: '+237', name: 'Cameroon', flag: '🇨🇲' },
  { code: 'CL', dial: '+56', name: 'Chile', flag: '🇨🇱' },
  { code: 'CN', dial: '+86', name: 'China', flag: '🇨🇳' },
  { code: 'CO', dial: '+57', name: 'Colombia', flag: '🇨🇴' },
  { code: 'CR', dial: '+506', name: 'Costa Rica', flag: '🇨🇷' },
  { code: 'HR', dial: '+385', name: 'Croatia', flag: '🇭🇷' },
  { code: 'CU', dial: '+53', name: 'Cuba', flag: '🇨🇺' },
  { code: 'CY', dial: '+357', name: 'Cyprus', flag: '🇨🇾' },
  { code: 'CZ', dial: '+420', name: 'Czech Republic', flag: '🇨🇿' },
  { code: 'DK', dial: '+45', name: 'Denmark', flag: '🇩🇰' },
  { code: 'DO', dial: '+1', name: 'Dominican Republic', flag: '🇩🇴' },
  { code: 'EC', dial: '+593', name: 'Ecuador', flag: '🇪🇨' },
  { code: 'EG', dial: '+20', name: 'Egypt', flag: '🇪🇬' },
  { code: 'ET', dial: '+251', name: 'Ethiopia', flag: '🇪🇹' },
  { code: 'FI', dial: '+358', name: 'Finland', flag: '🇫🇮' },
  { code: 'FR', dial: '+33', name: 'France', flag: '🇫🇷' },
  { code: 'GE', dial: '+995', name: 'Georgia', flag: '🇬🇪' },
  { code: 'DE', dial: '+49', name: 'Germany', flag: '🇩🇪' },
  { code: 'GH', dial: '+233', name: 'Ghana', flag: '🇬🇭' },
  { code: 'GR', dial: '+30', name: 'Greece', flag: '🇬🇷' },
  { code: 'GT', dial: '+502', name: 'Guatemala', flag: '🇬🇹' },
  { code: 'HK', dial: '+852', name: 'Hong Kong', flag: '🇭🇰' },
  { code: 'HU', dial: '+36', name: 'Hungary', flag: '🇭🇺' },
  { code: 'IS', dial: '+354', name: 'Iceland', flag: '🇮🇸' },
  { code: 'ID', dial: '+62', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'IR', dial: '+98', name: 'Iran', flag: '🇮🇷' },
  { code: 'IQ', dial: '+964', name: 'Iraq', flag: '🇮🇶' },
  { code: 'IE', dial: '+353', name: 'Ireland', flag: '🇮🇪' },
  { code: 'IL', dial: '+972', name: 'Israel', flag: '🇮🇱' },
  { code: 'IT', dial: '+39', name: 'Italy', flag: '🇮🇹' },
  { code: 'JM', dial: '+1', name: 'Jamaica', flag: '🇯🇲' },
  { code: 'JP', dial: '+81', name: 'Japan', flag: '🇯🇵' },
  { code: 'JO', dial: '+962', name: 'Jordan', flag: '🇯🇴' },
  { code: 'KZ', dial: '+7', name: 'Kazakhstan', flag: '🇰🇿' },
  { code: 'KE', dial: '+254', name: 'Kenya', flag: '🇰🇪' },
  { code: 'KW', dial: '+965', name: 'Kuwait', flag: '🇰🇼' },
  { code: 'LV', dial: '+371', name: 'Latvia', flag: '🇱🇻' },
  { code: 'LB', dial: '+961', name: 'Lebanon', flag: '🇱🇧' },
  { code: 'LY', dial: '+218', name: 'Libya', flag: '🇱🇾' },
  { code: 'LT', dial: '+370', name: 'Lithuania', flag: '🇱🇹' },
  { code: 'LU', dial: '+352', name: 'Luxembourg', flag: '🇱🇺' },
  { code: 'MY', dial: '+60', name: 'Malaysia', flag: '🇲🇾' },
  { code: 'MV', dial: '+960', name: 'Maldives', flag: '🇲🇻' },
  { code: 'MT', dial: '+356', name: 'Malta', flag: '🇲🇹' },
  { code: 'MX', dial: '+52', name: 'Mexico', flag: '🇲🇽' },
  { code: 'MA', dial: '+212', name: 'Morocco', flag: '🇲🇦' },
  { code: 'MM', dial: '+95', name: 'Myanmar', flag: '🇲🇲' },
  { code: 'NP', dial: '+977', name: 'Nepal', flag: '🇳🇵' },
  { code: 'NL', dial: '+31', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'NZ', dial: '+64', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'NG', dial: '+234', name: 'Nigeria', flag: '🇳🇬' },
  { code: 'NO', dial: '+47', name: 'Norway', flag: '🇳🇴' },
  { code: 'OM', dial: '+968', name: 'Oman', flag: '🇴🇲' },
  { code: 'PK', dial: '+92', name: 'Pakistan', flag: '🇵🇰' },
  { code: 'PA', dial: '+507', name: 'Panama', flag: '🇵🇦' },
  { code: 'PY', dial: '+595', name: 'Paraguay', flag: '🇵🇾' },
  { code: 'PE', dial: '+51', name: 'Peru', flag: '🇵🇪' },
  { code: 'PH', dial: '+63', name: 'Philippines', flag: '🇵🇭' },
  { code: 'PL', dial: '+48', name: 'Poland', flag: '🇵🇱' },
  { code: 'PT', dial: '+351', name: 'Portugal', flag: '🇵🇹' },
  { code: 'QA', dial: '+974', name: 'Qatar', flag: '🇶🇦' },
  { code: 'RO', dial: '+40', name: 'Romania', flag: '🇷🇴' },
  { code: 'RU', dial: '+7', name: 'Russia', flag: '🇷🇺' },
  { code: 'SA', dial: '+966', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: 'RS', dial: '+381', name: 'Serbia', flag: '🇷🇸' },
  { code: 'SG', dial: '+65', name: 'Singapore', flag: '🇸🇬' },
  { code: 'SK', dial: '+421', name: 'Slovakia', flag: '🇸🇰' },
  { code: 'SI', dial: '+386', name: 'Slovenia', flag: '🇸🇮' },
  { code: 'ZA', dial: '+27', name: 'South Africa', flag: '🇿🇦' },
  { code: 'KR', dial: '+82', name: 'South Korea', flag: '🇰🇷' },
  { code: 'ES', dial: '+34', name: 'Spain', flag: '🇪🇸' },
  { code: 'LK', dial: '+94', name: 'Sri Lanka', flag: '🇱🇰' },
  { code: 'SD', dial: '+249', name: 'Sudan', flag: '🇸🇩' },
  { code: 'SE', dial: '+46', name: 'Sweden', flag: '🇸🇪' },
  { code: 'CH', dial: '+41', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'SY', dial: '+963', name: 'Syria', flag: '🇸🇾' },
  { code: 'TW', dial: '+886', name: 'Taiwan', flag: '🇹🇼' },
  { code: 'TZ', dial: '+255', name: 'Tanzania', flag: '🇹🇿' },
  { code: 'TH', dial: '+66', name: 'Thailand', flag: '🇹🇭' },
  { code: 'TN', dial: '+216', name: 'Tunisia', flag: '🇹🇳' },
  { code: 'TR', dial: '+90', name: 'Turkey', flag: '🇹🇷' },
  { code: 'UG', dial: '+256', name: 'Uganda', flag: '🇺🇬' },
  { code: 'UA', dial: '+380', name: 'Ukraine', flag: '🇺🇦' },
  { code: 'AE', dial: '+971', name: 'United Arab Emirates', flag: '🇦🇪' },
  { code: 'UY', dial: '+598', name: 'Uruguay', flag: '🇺🇾' },
  { code: 'UZ', dial: '+998', name: 'Uzbekistan', flag: '🇺🇿' },
  { code: 'VE', dial: '+58', name: 'Venezuela', flag: '🇻🇪' },
  { code: 'VN', dial: '+84', name: 'Vietnam', flag: '🇻🇳' },
  { code: 'YE', dial: '+967', name: 'Yemen', flag: '🇾🇪' },
  { code: 'ZM', dial: '+260', name: 'Zambia', flag: '🇿🇲' },
  { code: 'ZW', dial: '+263', name: 'Zimbabwe', flag: '🇿🇼' },
];

/**
 * Full, ordered phone-country list — popular block first, then alphabetical.
 * Consumed by `PhoneInput` for the dropdown options and by `splitPhoneValue`
 * for matching a stored `+91…` string back to a country row.
 */
export const PHONE_COUNTRIES: PhoneCountry[] = [...POPULAR, ...REST];

/** Default selection if the form has no stored country / phone yet. */
export const DEFAULT_PHONE_COUNTRY: PhoneCountry =
  PHONE_COUNTRIES.find((c) => c.code === 'IN') ?? PHONE_COUNTRIES[0];

/**
 * Look up a `PhoneCountry` by its ISO-2 country code (e.g. `'US'`, `'IN'`,
 * `'GB'`). Case-insensitive. Returns `undefined` when the code is unknown so
 * callers can fall back to {@link DEFAULT_PHONE_COUNTRY}.
 */
export function findCountryByCode(code: unknown): PhoneCountry | undefined {
  if (!code) return undefined;
  const needle = String(code).trim().toUpperCase();
  if (!needle) return undefined;
  return PHONE_COUNTRIES.find((c) => c.code === needle);
}

/**
 * Pick the best matching `PhoneCountry` for a stored combined value like
 * `+919876543210`. Tries longest-prefix-first so `+1` (US) doesn't shadow
 * `+12` etc. Falls back to {@link DEFAULT_PHONE_COUNTRY} when there's no `+`
 * prefix or no match.
 */
export function matchCountryByDial(value: unknown): PhoneCountry {
  if (value === null || value === undefined || value === '') {
    return DEFAULT_PHONE_COUNTRY;
  }
  // The backend has historically stored `phone` as either a string or a number
  // depending on the row's age — coerce defensively so we don't blow up on
  // `value.trim()` for numeric values.
  const trimmed = String(value).trim().replace(/[\s-]/g, '');
  if (!trimmed) return DEFAULT_PHONE_COUNTRY;

  // Longest-prefix-first so `+12` doesn't shadow `+1`.
  const byLongest = [...PHONE_COUNTRIES].sort(
    (a, b) => b.dial.length - a.dial.length,
  );

  // With explicit `+` — straight prefix match.
  if (trimmed.startsWith('+')) {
    for (const c of byLongest) {
      if (trimmed.startsWith(c.dial)) return c;
    }
    return DEFAULT_PHONE_COUNTRY;
  }

  // Without `+` — the backend often strips it on storage so `+919876543210`
  // comes back as `919876543210`. Match the dial code's bare digits as a
  // prefix, but only if there's a plausible national number left (≥ 7 digits)
  // so we don't mis-classify a 10-digit local number like "5551234567" as a
  // country code + 7-digit number.
  for (const c of byLongest) {
    const dialDigits = c.dial.replace(/^\+/, '');
    if (
      dialDigits.length > 0 &&
      trimmed.startsWith(dialDigits) &&
      trimmed.length - dialDigits.length >= 10
    ) {
      return c;
    }
  }
  return DEFAULT_PHONE_COUNTRY;
}

/**
 * Split a stored combined value (e.g. `'+919876543210'`) into its dial-code
 * country + the bare national number. Strips any leading whitespace / dashes
 * the legacy data may have crept in with so the input shows only digits.
 */
export function splitPhoneValue(value: unknown): {
  country: PhoneCountry;
  national: string;
} {
  const country = matchCountryByDial(value);
  const raw =
    value === null || value === undefined
      ? ''
      : String(value).trim().replace(/[\s-]/g, '');
  // Strip the dial code in BOTH shapes — with `+` (`+919876…`) and without
  // (`919876…`) — so a backend round-trip that dropped the `+` doesn't make
  // the national number get the country code re-prepended on next save.
  let national = raw;
  if (raw.startsWith(country.dial)) {
    national = raw.slice(country.dial.length);
  } else {
    const dialDigits = country.dial.replace(/^\+/, '');
    if (
      dialDigits.length > 0 &&
      raw.startsWith(dialDigits) &&
      raw.length - dialDigits.length >= 10
    ) {
      national = raw.slice(dialDigits.length);
    }
  }
  return { country, national };
}

/**
 * Combine a dial code + national number back into the canonical payload string
 * (`+<dial><national>`). Returns `''` when both halves are empty so the form
 * can drop the field entirely on submit (matching the legacy `phone ||
 * undefined` behaviour).
 */
export function combinePhoneValue(
  country: PhoneCountry,
  national: string,
): string {
  const digits = (national ?? '').replace(/[^\d]/g, '');
  if (!digits) return '';
  return `${country.dial}${digits}`;
}

/** E.164 baseline — used when a country doesn't define `mobileLen`. */
const E164_MIN = 7;
const E164_MAX = 15;

/**
 * Validate a combined phone value (e.g. `'+919876543210'`) against the
 * selected country's expected national-number length. Returns `null` when
 * the value is valid or empty (treat empty as "no phone"), or a
 * user-facing error string when it's the wrong length for the country.
 */
export function validatePhoneByCountry(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const { country, national } = splitPhoneValue(raw);
  const digits = national.replace(/[^\d]/g, '');
  if (!digits) return 'Enter a phone number.';

  const spec = country.mobileLen;
  if (typeof spec === 'number') {
    if (digits.length !== spec) {
      return `${country.name} phone numbers must be ${spec} digits.`;
    }
  } else if (Array.isArray(spec)) {
    const [min, max] = spec;
    if (digits.length < min || digits.length > max) {
      return min === max
        ? `${country.name} phone numbers must be ${min} digits.`
        : `${country.name} phone numbers must be ${min}–${max} digits.`;
    }
  } else {
    if (digits.length < E164_MIN || digits.length > E164_MAX) {
      return `Phone numbers must be ${E164_MIN}–${E164_MAX} digits.`;
    }
  }
  return null;
}
